import { FC, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectIsOwner, selectMerchantCustomizations } from 'containers/app/appSlice';
import { useCreateCapitalSessionTokenMutation } from './api';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectComponentsProvider } from '@stripe/react-connect-js';
import CapitalComponents from './components/CapitalComponents';
import { selectWebViewTeamId } from 'containers/auth/authSlice';
import { useNavigate } from 'react-router-dom';
import { selectCapitalFinancingEnabled } from './capitalSlice';
import routesPath from 'routes/RoutesPath';
import { Loader } from 'components';
import { WebviewFunctions } from 'constants/common';

// This component is used by both webview and non-webview routes, test webview too in case of any changes in future
interface CapitalProps {
  isWebView?: boolean;
}

const Capital: FC<CapitalProps> = ({ isWebView = false }) => {
  const navigate = useNavigate();

  const currentTeam = useAppSelector(selectCurrentTeam);
  const webviewTeamId = useAppSelector(selectWebViewTeamId);
  const isOwner = useAppSelector(selectIsOwner);
  const { teamFeatures } = useAppSelector(selectMerchantCustomizations) ?? {};
  const isCapitalFinancingEnabled = useAppSelector(selectCapitalFinancingEnabled);

  const [createCapitalSessionToken, { isLoading: isTokenLoading }] = useCreateCapitalSessionTokenMutation();
  const [stripeConnectInstance, setStripeConnectInstance] = useState<any>(null);
  const [isLoadingStripeConnect, setIsLoadingStripeConnect] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const clientSecretRef = useRef<string | null>(null);
  const initializationCompleteRef = useRef(false);

  const fetchClientSecret = async () => {
    if (!(currentTeam?.id || webviewTeamId)) return null;

    if (!clientSecretRef.current) {
      try {
        const result = await createCapitalSessionToken({
          teamId: webviewTeamId ? webviewTeamId : currentTeam.id
        }).unwrap();
        clientSecretRef.current = result.capitalFinancingSessionClientSecret;
      } catch (error) {
        //error
      }
    }

    return clientSecretRef.current;
  };

  // Function to initialize the Stripe Connect instance
  const initializeStripeConnect = async () => {
    if (!(currentTeam?.id || webviewTeamId) || initializationCompleteRef.current) return;
    initializationCompleteRef.current = true;
    try {
      const result = await createCapitalSessionToken({
        teamId: webviewTeamId ? webviewTeamId : currentTeam.id
      }).unwrap();
      clientSecretRef.current = result.capitalFinancingSessionClientSecret;

      const publishableKey = result.stripePublishableKey;

      const connectInstance = await loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret
      });
      setStripeConnectInstance(connectInstance);
    } catch (error) {
      initializationCompleteRef.current = false;
    } finally {
      setIsLoadingStripeConnect(false);
    }
  };

  useEffect(
    () => () => {
      if (stripeConnectInstance) stripeConnectInstance.logout();
    },
    []
  );

  useEffect(() => {
    if (isWebView) {
      if (window?.flutter_inappwebview?.callHandler) {
        window.flutter_inappwebview.callHandler(WebviewFunctions.WEBVIEW_LOADED);
      }
      return;
    }
    if (!isOwner || !teamFeatures?.financingEnabled || !isCapitalFinancingEnabled) {
      navigate(routesPath.HOME);
    }
  }, [isOwner, teamFeatures?.financingEnabled, isCapitalFinancingEnabled]);

  useEffect(() => {
    if (currentTeam?.id || webviewTeamId) {
      initializationCompleteRef.current = false;
      initializeStripeConnect();
    }
  }, [currentTeam?.id, webviewTeamId]);

  const reloadCapital = () => {
    // Force component re-render by updating the key
    setRefreshKey(prev => prev + 1);

    // Reset the Stripe Connect instance
    if (stripeConnectInstance) {
      stripeConnectInstance.logout();
    }

    // Clear the cached client secret
    clientSecretRef.current = null;
    initializationCompleteRef.current = false;

    // Reinitialize Stripe Connect
    initializeStripeConnect();
  };

  const isLoading = isTokenLoading || isLoadingStripeConnect;

  return (
    <div className="h-full w-full px-[30px] pt-5">
      {!webviewTeamId && (
        <div className="mb-6 flex items-center justify-between">
          <div className="text-2xl font-bold text-primaryText">Financing</div>
        </div>
      )}
      <div className="pb-10">
        {isLoading ? (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <Loader />
          </div>
        ) : (
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            <CapitalComponents reloadCapital={reloadCapital} refreshKey={refreshKey} />
          </ConnectComponentsProvider>
        )}
      </div>
    </div>
  );
};

export default Capital;
